import type {
  ChannelThreadId,
  ShopperAiIntegrationId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type {
  CreateDraftSupportTicketInput,
  CreateDraftThreadInput,
  PublishDraftSupportTicketInput,
  StartSupportTicketInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { conversationQueryKeys, draftQueryKeys } from "../db/collections";
import { catchApiErrors } from "../db/error-handling";
import { useGlobalSupportTicketCollections } from "../providers/global-support-ticket-collections-context";

export function useStartSupportTicket() {
  const effectApi = useRequiredContext(EffectApiClientContext);

  const mutation = useMutation({
    mutationFn: async (input: StartSupportTicketInput) => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrors(
          client.supportTicket.startSupportTicket({ payload: input }),
        ),
      );
    },
    onSuccess: () => {},
  });

  return {
    startSupportTicket: mutation.mutateAsync,
    isStarting: mutation.isPending,
  };
}

export function useCreateDraftSupportTicket() {
  const effectApi = useRequiredContext(EffectApiClientContext);

  const mutation = useMutation({
    mutationFn: async (input: CreateDraftSupportTicketInput) => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrors(
          client.supportTicket.createDraftSupportTicket({ payload: input }),
        ),
      );
    },
    onSuccess: () => {},
  });

  return {
    createDraftSupportTicket: mutation.mutateAsync,
    isCreatingDraft: mutation.isPending,
  };
}

export function useCreateSupportTicketDraftThread() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryClient = useQueryClient();
  const collections = useGlobalSupportTicketCollections();

  const mutation = useMutation({
    mutationFn: async ({
      supportTicketId,
      input,
    }: {
      supportTicketId: SupportTicketId;
      input: CreateDraftThreadInput;
    }) => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrors(
          client.supportTicket.createSupportTicketDraftThread({
            path: { id: supportTicketId },
            payload: input,
          }),
        ),
      );
    },
    onSuccess: (data, { supportTicketId }) => {
      // Write the new thread directly into the collection so useLiveQuery
      // picks it up immediately — don't wait for invalidate→refetch.
      collections.threads.utils.writeUpsert({
        ...data.thread,
        supportTicketId,
      });
      void queryClient.invalidateQueries({
        queryKey: conversationQueryKeys.threads(),
      });
      void queryClient.invalidateQueries({ queryKey: draftQueryKeys.all });
    },
  });

  return {
    createSupportTicketDraftThread: mutation.mutateAsync,
    isCreatingDraftThread: mutation.isPending,
  };
}

export function useSaveSupportTicketDraft() {
  const effectApi = useRequiredContext(EffectApiClientContext);

  const mutation = useMutation({
    mutationFn: async ({
      supportTicketId,
      threadId,
      content,
      cc,
      bcc,
    }: {
      supportTicketId: SupportTicketId;
      threadId: ChannelThreadId;
      content: string;
      cc?: { email: string; name: string | null }[];
      bcc?: { email: string; name: string | null }[];
    }) => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrors(
          client.supportTicket.saveSupportTicketDraft({
            path: { id: supportTicketId },
            payload: {
              threadId,
              content,
              attachments: [],
              channel: "gmail",
              channelData: { cc, bcc },
            },
          }),
        ),
      );
    },
    onSuccess: (_data, variables) => {},
  });

  return {
    saveSupportTicketDraft: mutation.mutateAsync,
    isSavingDraft: mutation.isPending,
  };
}

export function usePublishDraftSupportTicket() {
  const effectApi = useRequiredContext(EffectApiClientContext);

  const mutation = useMutation({
    mutationFn: async ({
      supportTicketId,
      ...input
    }: PublishDraftSupportTicketInput & {
      supportTicketId: SupportTicketId;
    }) => {
      const client = effectApi.getClient();
      return await effectApi.runPromise(
        catchApiErrors(
          client.supportTicket.publishDraftSupportTicket({
            path: { id: supportTicketId },
            payload: input,
          }),
        ),
      );
    },
    onSuccess: () => {},
  });

  return {
    publishDraftSupportTicket: mutation.mutateAsync,
    isPublishing: mutation.isPending,
  };
}

interface CreateAndPublishInput {
  integrationType: "gmail" | "outlook";
  integrationId: ShopperAiIntegrationId;
  existingSupportTicketId?: SupportTicketId;
  primaryEmail: string;
  subject: string;
  content: string;
  to: { email: string; name: string | null }[];
  cc?: { email: string; name: string | null }[];
  bcc?: { email: string; name: string | null }[];
  onCreated: (supportTicketId: string) => void;
}

export function useCreateAndPublishSupportTicket() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createAndPublish = async ({
    integrationType,
    integrationId,
    existingSupportTicketId,
    primaryEmail,
    subject,
    content,
    to,
    cc,
    bcc,
    onCreated,
  }: CreateAndPublishInput) => {
    setIsSubmitting(true);
    try {
      const client = effectApi.getClient();

      let supportTicketId: SupportTicketId;
      if (existingSupportTicketId) {
        supportTicketId = existingSupportTicketId;
      } else {
        const draftResult = await effectApi.runPromise(
          catchApiErrors(
            client.supportTicket.createDraftSupportTicket({
              payload: {
                integrationId,
                customerEmail: primaryEmail || undefined,
                subject: subject || undefined,
              },
            }),
          ),
        );
        supportTicketId = draftResult.supportTicketId;
      }

      await effectApi.runPromise(
        catchApiErrors(
          client.supportTicket.publishDraftSupportTicket({
            path: { id: supportTicketId },
            payload: {
              customerEmail: primaryEmail,
              subject,
              content,
              attachments: [],
              to,
              cc,
              bcc,
            },
          }),
        ),
      );

      onCreated(supportTicketId);
      void queryClient.invalidateQueries({
        queryKey: conversationQueryKeys.all,
      });
    } catch (error) {
      console.error("Failed to create and publish support ticket", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return { createAndPublish, isSubmitting };
}
